<template>
  <div class="fb-canvas">
    <div
      v-if="!fields.length"
      class="fb-canvas-empty"
    >
      <feather-icon
        icon="LayoutIcon"
        size="34"
        class="mb-1"
      />
      <h6 class="mb-50">
        Formulário vazio
      </h6>
      <p class="mb-0">
        Escolha um tipo de campo na paleta à esquerda para começar a montar.
      </p>
    </div>

    <draggable
      v-else
      v-model="localOrder"
      tag="div"
      handle=".drag-handle"
      ghost-class="fb-canvas-ghost"
      @end="onDragEnd"
    >
      <div
        v-for="(field, idx) in localOrder"
        :key="field.id"
      >
        <!-- section_break: divisor visual, sem badge/lógica -->
        <div
          v-if="field.field_type === 'section_break'"
          class="fb-section-break"
          :class="{ 'fb-section-break--selected': field.id === selectedFieldId }"
          @click="$emit('select', field.id)"
        >
          <feather-icon
            icon="MenuIcon"
            size="14"
            class="drag-handle text-muted mr-2"
            style="cursor: grab"
          />
          <hr class="flex-grow-1 mx-2 mb-0">
          <span class="font-weight-bold text-uppercase small text-muted">{{ field.label }}</span>
          <hr class="flex-grow-1 mx-2 mb-0">
        </div>

        <!-- card de pergunta -->
        <div
          v-else
          class="card fb-field-card mb-2"
          :class="{ 'fb-field-card--selected': field.id === selectedFieldId }"
        >
          <div class="card-body py-2 px-3 d-flex align-items-start">
            <feather-icon
              icon="MenuIcon"
              size="14"
              class="drag-handle text-muted mt-1 mr-2"
              style="cursor: grab"
            />
            <div
              class="flex-grow-1 fb-field-card__body"
              @click="$emit('select', field.id)"
            >
              <div class="d-flex align-items-center">
                <span class="text-muted small mr-2">#{{ idx + 1 }}</span>
                <span class="font-weight-bold">{{ field.label }}</span>
                <feather-icon
                  v-if="field.is_root"
                  icon="StarIcon"
                  size="12"
                  class="text-warning ml-50"
                  title="Campo inicial"
                />
              </div>
              <div class="mt-50">
                <b-badge
                  variant="light-info"
                  class="mr-50"
                >
                  {{ typeLabel(field.field_type) }}
                </b-badge>
                <b-badge
                  v-if="field.required"
                  variant="light-danger"
                >
                  Obrigatória
                </b-badge>
              </div>

              <!-- chips de lógica — boolean -->
              <div
                v-if="field.field_type === 'boolean' && (field.next_field_id_if_yes || field.next_field_id_if_no)"
                class="mt-50"
              >
                <div
                  v-if="field.next_field_id_if_yes"
                  class="fb-chip"
                >
                  <feather-icon
                    icon="CornerDownRightIcon"
                    size="11"
                    class="mr-25"
                  />
                  Se Sim → {{ fieldLabelById(field.next_field_id_if_yes) }}
                  <button
                    type="button"
                    class="fb-chip__btn"
                    title="Editar lógica"
                    @click.stop="$emit('edit-logic', field.id)"
                  >
                    <feather-icon
                      icon="EditIcon"
                      size="10"
                    />
                  </button>
                  <button
                    type="button"
                    class="fb-chip__btn"
                    title="Remover"
                    @click.stop="$emit('clear-branch', { fieldId: field.id, branch: 'yes' })"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="10"
                    />
                  </button>
                </div>
                <div
                  v-if="field.next_field_id_if_no"
                  class="fb-chip"
                >
                  <feather-icon
                    icon="CornerDownRightIcon"
                    size="11"
                    class="mr-25"
                  />
                  Se Não → {{ fieldLabelById(field.next_field_id_if_no) }}
                  <button
                    type="button"
                    class="fb-chip__btn"
                    title="Editar lógica"
                    @click.stop="$emit('edit-logic', field.id)"
                  >
                    <feather-icon
                      icon="EditIcon"
                      size="10"
                    />
                  </button>
                  <button
                    type="button"
                    class="fb-chip__btn"
                    title="Remover"
                    @click.stop="$emit('clear-branch', { fieldId: field.id, branch: 'no' })"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="10"
                    />
                  </button>
                </div>
              </div>

              <!-- chips de lógica — single_choice -->
              <div
                v-else-if="field.field_type === 'single_choice' && optionsWithBranch(field).length"
                class="mt-50"
              >
                <div
                  v-for="opt in optionsWithBranch(field)"
                  :key="opt.id"
                  class="fb-chip"
                >
                  <feather-icon
                    icon="CornerDownRightIcon"
                    size="11"
                    class="mr-25"
                  />
                  Se "{{ opt.label }}" → {{ fieldLabelById(opt.next_field_id) }}
                  <button
                    type="button"
                    class="fb-chip__btn"
                    title="Editar lógica"
                    @click.stop="$emit('edit-logic', field.id)"
                  >
                    <feather-icon
                      icon="EditIcon"
                      size="10"
                    />
                  </button>
                  <button
                    type="button"
                    class="fb-chip__btn"
                    title="Remover"
                    @click.stop="$emit('clear-branch', { fieldId: field.id, branch: opt.id })"
                  >
                    <feather-icon
                      icon="XIcon"
                      size="10"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </draggable>
  </div>
</template>

<script>
import { BBadge } from 'bootstrap-vue'
import draggable from 'vuedraggable'
import { fieldTypeLabel } from './fieldTypes'

export default {
  name: 'FieldCanvas',
  components: { BBadge, draggable },
  props: {
    fields: { type: Array, default: () => [] },
    selectedFieldId: { type: [Number, String], default: null },
  },
  data() {
    return {
      // Cópia local só para a ordenação visual do drag&drop — evita mutar a
      // prop `fields` diretamente (o pai é a fonte da verdade; persistimos
      // via @reorder e ele nos devolve a lista já atualizada).
      localOrder: this.sortedFields(),
    }
  },
  watch: {
    fields() {
      this.localOrder = this.sortedFields()
    },
  },
  methods: {
    sortedFields() {
      return [...this.fields].sort((a, b) => (a.order || 0) - (b.order || 0))
    },
    typeLabel(type) {
      return fieldTypeLabel(type)
    },
    fieldLabelById(id) {
      const found = this.fields.find(f => f.id === id)
      return found ? found.label : '?'
    },
    optionsWithBranch(field) {
      return (field.options || []).filter(o => o.next_field_id)
    },
    onDragEnd() {
      this.$emit('reorder', this.localOrder.map((f, idx) => ({ id: f.id, order: idx })))
    },
  },
}
</script>

<style scoped>
.fb-canvas {
  min-height: 320px;
  padding: 12px;
  border-radius: 8px;
  background: #fbfbfd;
  border: 1px solid #ebe9f1;
}
.dark-layout .fb-canvas {
  background: #212840;
  border-color: #3b4253;
}

.fb-canvas-ghost {
  opacity: 0.55;
}
.fb-canvas-ghost .fb-field-card,
.fb-canvas-ghost .fb-section-break {
  border: 1px dashed #7367f0 !important;
  background: rgba(115, 103, 240, 0.08);
}
.fb-canvas-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 24px;
  color: #b9b9c3;
}
.fb-canvas-empty h6 {
  color: #6e6b7b;
}
.fb-canvas-empty p {
  font-size: 0.82rem;
  max-width: 300px;
}
.dark-layout .fb-canvas-empty h6 {
  color: #d0d2d6;
}

.fb-field-card {
  border: 1px solid #ebe9f1;
  border-left: 3px solid transparent;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.fb-field-card:hover {
  border-left-color: #c3bdf7;
}
.fb-field-card--selected {
  border-color: #7367f0;
  border-left: 3px solid #7367f0;
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
}
.dark-layout .fb-field-card {
  border-color: #3b4253;
}

.fb-field-card__body {
  cursor: pointer;
}

.drag-handle:active {
  cursor: grabbing !important;
}
.fb-section-break {
  display: flex;
  align-items: center;
  padding: 6px 4px;
  margin-bottom: 8px;
  cursor: pointer;
  border-radius: 4px;
}
.fb-section-break--selected {
  background: rgba(115, 103, 240, 0.06);
}
.fb-chip {
  display: inline-flex;
  align-items: center;
  background: #f3f2f7;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.75rem;
  color: #6e6b7b;
  margin-right: 6px;
  margin-top: 4px;
}
.fb-chip__btn {
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  padding: 0 2px;
  margin-left: 4px;
  color: inherit;
  line-height: 1;
}
.dark-layout .fb-chip {
  background: #3b4253;
  color: #d0d2d6;
}
</style>
